import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOut, List, X } from "@phosphor-icons/react";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationCenter from "@/components/NotificationCenter";
import DriverPanel from "@/pages/panels/DriverPanel";
import RunnerPanel from "@/pages/panels/RunnerPanel";
import AdminPanel from "@/pages/panels/AdminPanel";
import CeoPanel from "@/pages/panels/CeoPanel";
import DevPanel from "@/pages/panels/DevPanel";
import StaffPanel from "@/pages/panels/StaffPanel";
import PayrollPanel from "@/pages/panels/PayrollPanel";
import InvoicesPanel from "@/pages/panels/InvoicesPanel";
import SettingsPanel from "@/pages/panels/SettingsPanel";
import ProfilePanel from "@/pages/panels/ProfilePanel";
import VehiclesPanel from "@/pages/panels/VehiclesPanel";
import ApprovalsPanel from "@/pages/panels/ApprovalsPanel";
import InspectionPanel from "@/pages/panels/InspectionPanel";
import FuelPanel from "@/pages/panels/FuelPanel";
import ExpensesPanel from "@/pages/panels/ExpensesPanel";
import InhouseListPanel from "@/pages/panels/InhouseListPanel";
import WorkshopPanel from "@/pages/panels/WorkshopPanel";

const ROLE_META = {
  driver: { label: "Driver", desc: "Submit and track your towing jobs" },
  runner: { label: "Runner", desc: "Jobs tagged to you" },
  finance: { label: "Finance", desc: "Payroll, invoices & expense tracking" },
  workshop: { label: "Workshop Partner", desc: "Vehicles referred to your workshop" },
  admin: { label: "Admin", desc: "Full dispatch & submission control" },
  ceo: { label: "CEO", desc: "Executive overview & financials" },
  coceo: { label: "Co-CEO", desc: "Executive overview & financials" },
  dev: { label: "Developer", desc: "System administration" },
};

const NAV_ITEMS = {
  driver: [["main", "Jobs"], ["inspection", "Inspection"], ["fuel", "Fuel Log"], ["invoices", "Invoices"], ["payroll", "Payroll"], ["profile", "My Profile"]],
  runner: [["main", "Jobs"], ["invoices", "Invoices"], ["payroll", "Payroll"], ["profile", "My Profile"]],
  finance: [["main", "Payroll"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["profile", "My Profile"]],
  workshop: [["main", "Jobs"], ["profile", "My Profile"]],
  admin: [["main", "Overview"], ["jobs", "Jobs"], ["approvals", "Approvals"], ["staff", "Staff"], ["payroll", "Payroll"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["inhouse", "Inhouse List"], ["vehicles", "Vehicles"], ["profile", "My Profile"]],
  ceo: [["main", "Analytics"], ["jobs", "Jobs"], ["approvals", "Approvals"], ["staff", "Staff"], ["payroll", "Payroll"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["inhouse", "Inhouse List"], ["vehicles", "Vehicles"], ["settings", "Settings"], ["profile", "My Profile"]],
  coceo: [["main", "Analytics"], ["jobs", "Jobs"], ["approvals", "Approvals"], ["staff", "Staff"], ["payroll", "Payroll"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["inhouse", "Inhouse List"], ["vehicles", "Vehicles"], ["settings", "Settings"], ["profile", "My Profile"]],
  dev: [["main", "System"], ["jobs", "Jobs"], ["staff", "Staff"], ["payroll", "Payroll"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["inhouse", "Inhouse List"], ["vehicles", "Vehicles"], ["settings", "Settings"], ["profile", "My Profile"]],
};

const MGMT = ["admin", "ceo", "coceo", "dev"];
const CAN_MARK_PAID = ["admin", "ceo", "coceo", "finance"];

function Panel({ role, user, staff }) {
  if (role === "driver") return <DriverPanel user={user} staff={staff} />;
  if (role === "runner") return <RunnerPanel />;
  if (role === "finance") return <PayrollPanel canMarkPaid />;
  if (role === "workshop") return <WorkshopPanel user={user} />;
  if (role === "admin") return <AdminPanel staff={staff} />;
  if (role === "ceo" || role === "coceo") return <CeoPanel staff={staff} />;
  if (role === "dev") return <DevPanel />;
  return null;
}

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [section, setSection] = useState("main");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="dashboard-loading">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const meta = ROLE_META[user.role] || { label: user.role, desc: "" };
  const navItems = NAV_ITEMS[user.role] || [["main", "Dashboard"]];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <button className="rounded-sm border border-input p-2 lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button">
              {menuOpen ? <X size={18} /> : <List size={18} />}
            </button>
            <img src={LOGO_URL} alt="PSS Towing Auto Assist" className="h-10 w-10 rounded-sm object-contain" data-testid="dashboard-logo" />
            <div className="hidden sm:block">
              <p className="font-display text-xs font-black uppercase tracking-widest">PSS Towing Auto Assist</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{meta.label} Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationCenter />
            <div className="hidden text-right sm:block">
              <p className="text-sm font-bold" data-testid="user-name">{user.name}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{user.email}</p>
            </div>
            <Button data-testid="logout-button" onClick={handleLogout} variant="outline"
              className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
              <SignOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="border-b border-border bg-card px-6 py-4 lg:hidden">
          <p className="text-sm font-bold">{user.name}</p>
          <p className="text-xs text-muted-foreground">{meta.label} · {user.email}</p>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        <div className="mb-8 animate-fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary">{meta.label} Dashboard</p>
          <h1 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="dashboard-title">
            {meta.desc}
          </h1>
        </div>
        {user.is_active === false ? (
          <div className="space-y-6" data-testid="deactivated-notice">
            <div className="rounded-sm border border-red-500/40 bg-red-500/10 p-6">
              <p className="font-display text-lg font-bold text-red-400">Account deactivated</p>
              <p className="mt-1 text-sm text-muted-foreground">
                You retain access to your Payroll for 1 month after deactivation. All other sections are locked.
              </p>
            </div>
            <ProfilePanel user={user} payslipsOnly />
          </div>
        ) : !user.profile_complete ? (
          <ProfilePanel user={user} forced />
        ) : (
          <>
            <nav className="mb-8 flex flex-wrap gap-2" data-testid="dashboard-nav">
              {navItems.map(([key, label]) => (
                <button key={key} data-testid={`nav-${key}`} onClick={() => setSection(key)}
                  className={`rounded-sm border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                    section === key
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}>
                  {label}
                </button>
              ))}
            </nav>
            {section === "main" && <Panel role={user.role} user={user} staff={staff} />}
            {section === "jobs" && <DriverPanel user={user} staff={staff} />}
            {section === "staff" && <StaffPanel userRole={user.role} />}
            {section === "approvals" && <ApprovalsPanel staff={staff} />}
            {section === "payroll" && ((MGMT.includes(user.role) || user.role === "finance")
              ? <PayrollPanel canMarkPaid={CAN_MARK_PAID.includes(user.role)} />
              : <ProfilePanel user={user} payslipsOnly />)}
            {section === "invoices" && <InvoicesPanel />}
            {section === "expenses" && <ExpensesPanel />}
            {section === "inhouse" && <InhouseListPanel userRole={user.role} />}
            {section === "vehicles" && <VehiclesPanel canEdit={["admin", "dev"].includes(user.role)} />}
            {section === "settings" && <SettingsPanel />}
            {section === "profile" && <ProfilePanel user={user} />}
            {section === "inspection" && <InspectionPanel />}
            {section === "fuel" && <FuelPanel />}
          </>
        )}
      </main>
    </div>
  );
}
